import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ConflictEntry, ScheduleDay } from "../lib/derive";
import type { ArtistDTO, EventDTO } from "../lib/api";
import type { EventPrefs, PreferenceMap, Rating, SetPreferenceMap } from "../lib/preferences";
import { setSetRating } from "../lib/preferences";
import { countPicks, decodePicks, shareForLineup } from "../lib/share";
import { ShareButton } from "../components/ShareButton";
import { ClashOff } from "./ClashOff";
import { dayLabels } from "../lib/format";
import {
  countPicksInSchedule,
  deleteSchedule,
  listSchedulesForEvent,
  saveSchedule,
  updateSchedule,
  MAX_NAME_LENGTH,
  type Schedule as SavedSchedule,
} from "../lib/schedules";

export function Schedule({
  schedule,
  conflicts,
  event,
  prefs,
  eventPrefs,
  onOpen,
  onReset,
  onImport,
  onLoadSchedule,
}: {
  schedule: ScheduleDay[];
  conflicts: ConflictEntry[];
  event: EventDTO | null;
  prefs: PreferenceMap;
  /** Full event prefs (including per-set overrides) — needed to snapshot. */
  eventPrefs: EventPrefs;
  onOpen: (slug: string) => void;
  onReset: () => void;
  onImport?: (next: PreferenceMap) => void;
  /** Replace the current working prefs with the given snapshot. */
  onLoadSchedule?: (next: EventPrefs) => void;
}) {
  // Day labels derived from the event's real startDate — so Tomorrowland
  // shows "Fri · Jul 17", not EDC's "Fri · May 15".
  const DAY_LABELS = useMemo(
    () => dayLabels(event?.startDate ?? "2026-01-01", schedule.length || 3),
    [event?.startDate, schedule.length],
  );
  const [viewDay, setViewDay] = useState<number | null>(null);
  const [importCandidate, setImportCandidate] = useState<PreferenceMap | null>(null);
  const [importSetCandidate, setImportSetCandidate] = useState<SetPreferenceMap | null>(null);
  const [importName, setImportName] = useState<string | null>(null);

  // Named saved schedules for this event. Reload whenever storage changes.
  const [savedSchedules, setSavedSchedules] = useState<SavedSchedule[]>(() =>
    event ? listSchedulesForEvent(event.slug) : [],
  );
  useEffect(() => {
    if (!event) return;
    setSavedSchedules(listSchedulesForEvent(event.slug));
    const onChange = () => setSavedSchedules(listSchedulesForEvent(event.slug));
    window.addEventListener("schedules:changed", onChange);
    return () => window.removeEventListener("schedules:changed", onChange);
  }, [event?.slug]);

  // Detect a pending import — either from the ShareLanding (short /s/token
  // URL) via sessionStorage, or from a legacy ?picks= query param.
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const raw = sessionStorage.getItem("checkthelineup:pendingImport");
      if (raw) {
        const parsed = JSON.parse(raw) as {
          picks?: PreferenceMap;
          setPicks?: SetPreferenceMap;
          eventSlug?: string;
          name?: string | null;
        };
        const hasArtists = parsed?.picks && Object.keys(parsed.picks).length > 0;
        const hasSets = parsed?.setPicks && Object.keys(parsed.setPicks).length > 0;
        if (hasArtists || hasSets) {
          setImportCandidate(parsed.picks ?? {});
          setImportSetCandidate(parsed.setPicks ?? {});
          setImportName(parsed.name ?? null);
          return;
        }
      }
    } catch { /* ignore */ }

    const params = new URLSearchParams(window.location.search);
    const encoded = params.get("picks");
    if (!encoded) return;
    const parsed = decodePicks(encoded);
    if (!parsed || Object.keys(parsed).length === 0) return;
    setImportCandidate(parsed);
  }, []);

  const dismissImport = () => {
    setImportCandidate(null);
    setImportSetCandidate(null);
    setImportName(null);
    // Clear both the sessionStorage handoff and the legacy ?picks= URL
    // so a refresh doesn't re-prompt the user.
    if (typeof window !== "undefined") {
      try { sessionStorage.removeItem("checkthelineup:pendingImport"); } catch { /* ignore */ }
      const u = new URL(window.location.href);
      u.searchParams.delete("picks");
      window.history.replaceState({}, "", u.toString());
    }
  };

  const acceptImport = () => {
    if (importCandidate && onImport) onImport(importCandidate);
    // Per-set imports (curator-mode shared schedules) bypass the artist
    // onImport callback and write directly to per-set storage.
    if (event && importSetCandidate) {
      for (const [idStr, rating] of Object.entries(importSetCandidate)) {
        const id = Number(idStr);
        if (Number.isFinite(id)) setSetRating(event.slug, id, rating);
      }
    }
    dismissImport();
  };

  const currentPicksCount = countPicks(prefs);

  const onSaveAs = useCallback(() => {
    if (!event || currentPicksCount === 0) return;
    const defaultName = `My ${event.name} lineup`;
    const raw = window.prompt(
      `Save this lineup as…  (max ${MAX_NAME_LENGTH} chars)`,
      defaultName,
    );
    if (raw === null) return; // cancelled
    const saved = saveSchedule({
      name: raw,
      eventSlug: event.slug,
      picks: eventPrefs,
    });
    if (!saved) window.alert("Please enter a name for the schedule.");
  }, [event, eventPrefs, currentPicksCount]);

  const onRenameSchedule = useCallback((s: SavedSchedule) => {
    const next = window.prompt("Rename schedule:", s.name);
    if (next === null) return;
    updateSchedule(s.id, { name: next });
  }, []);

  const onDeleteSchedule = useCallback((s: SavedSchedule) => {
    if (!window.confirm(`Delete "${s.name}"? This can't be undone.`)) return;
    deleteSchedule(s.id);
  }, []);

  const onLoad = useCallback(
    (s: SavedSchedule) => {
      if (!onLoadSchedule) return;
      const warn =
        currentPicksCount > 0
          ? `Replace your current picks (${currentPicksCount}) with "${s.name}"?`
          : `Load "${s.name}"?`;
      if (!window.confirm(warn)) return;
      onLoadSchedule(s.picks);
    },
    [onLoadSchedule, currentPicksCount],
  );

  const onOverwrite = useCallback(
    (s: SavedSchedule) => {
      if (!window.confirm(`Overwrite "${s.name}" with your current picks?`)) return;
      updateSchedule(s.id, { picks: eventPrefs });
    },
    [eventPrefs],
  );

  const conflictKeyFor = useMemo(() => {
    const set = new Set<string>();
    for (const c of conflicts) for (const a of c.artists) set.add(`${c.day}:${a.slug}`);
    return set;
  }, [conflicts]);

  const hasAnything = schedule.some(d => d.lanes.length);

  return (
    <div data-testid="view-schedule">
      <div className="section-head">
        <div>
          <span className="super">MY LINEUP</span>
          <h2>Your schedule</h2>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {event && hasAnything && (
            <>
              <button
                className="chip"
                onClick={onSaveAs}
                data-testid="save-as-schedule"
                title="Save this lineup as a named schedule"
              >
                Save as…
              </button>
              <ShareButton
                variant="chip"
                label="Share"
                data-testid="share-lineup"
                content={() => shareForLineup(event, eventPrefs)}
              />
            </>
          )}
          <button
            className="chip"
            onClick={() => {
              if (confirm("Clear all your ratings on this device?")) onReset();
            }}
            data-testid="reset-prefs"
          >
            reset
          </button>
        </div>
      </div>

      {event && savedSchedules.length > 0 && (
        <SavedSchedulesSection
          event={event}
          schedules={savedSchedules}
          onLoad={onLoad}
          onRename={onRenameSchedule}
          onDelete={onDeleteSchedule}
          onOverwrite={onOverwrite}
          currentPicksCount={currentPicksCount}
        />
      )}

      {importCandidate && (
        <ImportBanner
          picks={importCandidate}
          setPicks={importSetCandidate ?? {}}
          name={importName}
          onAccept={acceptImport}
          onDismiss={dismissImport}
        />
      )}

      <div className="chiprow" role="tablist" aria-label="Day filter">
        <button role="tab" className="chip" aria-pressed={viewDay === null} onClick={() => setViewDay(null)}>All days</button>
        {DAY_LABELS.map((label, i) => {
          const d = i + 1;
          return (
            <button key={d} role="tab" className="chip" aria-pressed={viewDay === d} onClick={() => setViewDay(d)}>
              {label}
            </button>
          );
        })}
      </div>

      {!hasAnything && (
        <div className="empty" data-testid="schedule-empty">
          <div className="emoji">🎚️</div>
          Nothing liked yet. Swipe right (or up!) on the deck to start building your schedule.
        </div>
      )}

      {schedule
        .filter(d => viewDay === null || d.day === viewDay)
        .map(day => (
          <section key={day.day} className="sch-day" data-testid={`sch-day-${day.day}`}>
            <div className="day-head">
              <span className="title">Day {day.day} · {DAY_LABELS[day.day - 1]}</span>
              <span className="lcd">{day.lanes.reduce((n, l) => n + l.sets.length, 0)} sets</span>
            </div>
            {day.lanes.map(lane => (
              <div key={lane.stage.slug} className="sch-lane">
                <div className="stage-chip" style={{ ["--stage-color" as never]: lane.stage.color ?? undefined }}>
                  <span className="dot" />{lane.stage.name}
                </div>
                {lane.sets.map(item => {
                  const isConflict = conflictKeyFor.has(`${day.day}:${item.artist.slug}`);
                  return (
                    <button
                      key={item.set.id}
                      className={`sch-item ${isConflict ? "conflict" : ""}`}
                      data-testid="sch-item"
                      data-slug={item.artist.slug}
                      onClick={() => onOpen(item.artist.slug)}
                    >
                      {item.set.startLabel && (
                        <span className="time">{item.set.startLabel} → {item.set.endLabel}</span>
                      )}
                      <span className="name">{item.artist.name}</span>
                      <span className="sch-badges">
                        {isConflict && (
                          <span
                            className="badge-conflict"
                            data-testid="badge-conflict"
                            title="Overlaps another set in your lineup"
                          >
                            CLASH
                          </span>
                        )}
                        {item.rating === "superlike" && <span className="badge-super">SUPER</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </section>
        ))}
    </div>
  );
}

function ImportBanner({
  picks,
  setPicks,
  name,
  onAccept,
  onDismiss,
}: {
  picks: PreferenceMap;
  setPicks?: SetPreferenceMap;
  name?: string | null;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  // Count both artist-level and per-set picks so curator-mode shares
  // (which are set-only) don't display "0 artists picked".
  const total =
    countPicks(picks) +
    (setPicks ? Object.values(setPicks).filter(r => r === "like" || r === "superlike").length : 0);
  return (
    <div
      className="import-banner"
      role="region"
      aria-label="Imported lineup"
      data-testid="import-banner"
    >
      <div>
        <strong>
          {name ? `Someone shared "${name}" with you.` : "Someone shared their lineup with you."}
        </strong>
        <div>
          {total} artist{total === 1 ? "" : "s"} picked. Import to see them in your schedule?
        </div>
      </div>
      <div className="import-banner-actions">
        <button className="chip" onClick={onDismiss} data-testid="import-dismiss">Dismiss</button>
        <button className="share-primary-btn" onClick={onAccept} data-testid="import-accept">
          Import
        </button>
      </div>
    </div>
  );
}

export function Conflicts({
  conflicts,
  artists,
  onOpen,
  onRate,
}: {
  conflicts: ConflictEntry[];
  artists: ArtistDTO[];
  onOpen: (slug: string) => void;
  onRate: (slug: string, rating: Rating | null) => void;
}) {
  const [active, setActive] = useState<number | null>(null);
  const activeConflict = active !== null ? conflicts[active] ?? null : null;

  // If the user resolves a clash and the conflicts array changes (the
  // resolved entry drops out), close any stale clash-off view.
  useEffect(() => {
    if (active !== null && active >= conflicts.length) setActive(null);
  }, [conflicts, active]);

  return (
    <div data-testid="view-conflicts">
      <div className="section-head">
        <div>
          <span className="super">CLASH FINDER</span>
          <h2>{conflicts.length} conflict{conflicts.length === 1 ? "" : "s"}</h2>
        </div>
      </div>

      {conflicts.length === 0 && (
        <div className="empty" data-testid="conflicts-empty">
          <div className="emoji">✨</div>
          No conflicts — your schedule is perfect so far.
        </div>
      )}

      {conflicts.map((c, i) => (
        <div key={i} className="conflict-card">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <h3 style={{ margin: 0 }}>
              Day {c.day}{c.startLabel ? ` · ${c.startLabel} → ${c.endLabel}` : ""}
            </h3>
            <button
              className="share-primary-btn"
              onClick={() => setActive(i)}
              data-testid="start-clashoff"
              data-conflict-index={i}
            >
              Clash-Off
            </button>
          </div>
          <div className="col" style={{ gap: 8 }}>
            {c.artists.map(a => (
              <button
                key={a.slug}
                className="sch-item"
                onClick={() => onOpen(a.slug)}
                data-testid="conflict-artist"
                data-slug={a.slug}
                style={{ textAlign: "left" }}
              >
                <span className="time">{a.stage}</span>
                <span className="name">{a.name}</span>
                {a.rating === "superlike" && <span className="badge-super">SUPER</span>}
              </button>
            ))}
          </div>
        </div>
      ))}

      {activeConflict && (
        <ClashOff
          conflict={activeConflict}
          artists={artists}
          onClose={() => setActive(null)}
          onRate={onRate}
        />
      )}
    </div>
  );
}

/**
 * Saved named schedules — a scrollable horizontal strip shown above the
 * day-filter chiprow. Each card has name / pick count / age and actions
 * (load, share, overwrite, rename, delete).
 */
function SavedSchedulesSection({
  event,
  schedules,
  onLoad,
  onRename,
  onDelete,
  onOverwrite,
  currentPicksCount,
}: {
  event: EventDTO;
  schedules: SavedSchedule[];
  onLoad: (s: SavedSchedule) => void;
  onRename: (s: SavedSchedule) => void;
  onDelete: (s: SavedSchedule) => void;
  onOverwrite: (s: SavedSchedule) => void;
  currentPicksCount: number;
}) {
  return (
    <section className="saved-schedules" data-testid="saved-schedules">
      <div className="saved-schedules-head">
        <span className="super">SAVED SCHEDULES</span>
        <span className="lcd">{schedules.length}</span>
      </div>
      <div className="saved-schedules-strip">
        {schedules.map(s => (
          <SavedScheduleCard
            key={s.id}
            event={event}
            schedule={s}
            canOverwrite={currentPicksCount > 0}
            onLoad={() => onLoad(s)}
            onRename={() => onRename(s)}
            onDelete={() => onDelete(s)}
            onOverwrite={() => onOverwrite(s)}
          />
        ))}
      </div>
    </section>
  );
}

function SavedScheduleCard({
  event,
  schedule,
  canOverwrite,
  onLoad,
  onRename,
  onDelete,
  onOverwrite,
}: {
  event: EventDTO;
  schedule: SavedSchedule;
  canOverwrite: boolean;
  onLoad: () => void;
  onRename: () => void;
  onDelete: () => void;
  onOverwrite: () => void;
}) {
  const pickCount = countPicksInSchedule(schedule);
  return (
    <div className="saved-schedule-card" data-testid="saved-schedule" data-id={schedule.id}>
      <div className="saved-schedule-head">
        <div className="saved-schedule-name" title={schedule.name}>
          {schedule.name}
        </div>
        <span className="lcd">{pickCount}</span>
      </div>
      <div className="saved-schedule-meta">
        updated {fmtAge(schedule.updatedAt)}
      </div>
      <div className="saved-schedule-actions">
        <button className="chip" onClick={onLoad} data-testid="saved-load">Load</button>
        <ShareButton
          variant="chip"
          label="Share"
          data-testid="saved-share"
          content={() =>
            // Pass the whole EventPrefs (artists + sets) — passing just
            // schedule.picks.artists drops curator-mode set-only picks
            // and shows "0 picks" in the share text.
            shareForLineup(event, schedule.picks, { name: schedule.name })
          }
        />
        {canOverwrite && (
          <button className="chip" onClick={onOverwrite} data-testid="saved-overwrite" title="Save the current picks into this schedule">
            Save
          </button>
        )}
        <button className="chip" onClick={onRename} data-testid="saved-rename">Rename</button>
        <button
          className="chip"
          onClick={onDelete}
          data-testid="saved-delete"
          style={{ color: "var(--red)" }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function fmtAge(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}
